import { z } from 'zod';
import { apiClient } from '.';
export const HANDLE_TIMEOUT_MS = 15000;
export const handleInputSchema = z.object({
    handle: z.string().trim().transform(value => value.replace(/^@+/, '').toLowerCase())
        .pipe(z.string().regex(/^[a-z0-9._-]{3,32}$/)),
    visibility: z.enum(['public', 'friends_of_friends', 'friends_only'])
});
const visibilitySchema = z.enum(['public', 'friends_of_friends', 'friends_only', 'private'])
    .transform(value => value === 'private' ? 'friends_only' : value);
// The existing endpoint returns database field names; the legacy camel-case contact contract is not its receipt.
const rowSchema = z.object({
    id: z.string().min(1), client_id: z.string().min(1), tenant_id: z.string().min(1),
    handle: z.string().regex(/^[a-z0-9._-]{3,32}$/), visibility: visibilitySchema,
    is_primary: z.boolean(), active: z.literal(true)
});
const listSchema = z.object({
    identity: z.object({ id: z.string().min(1), tenant_id: z.string().min(1) }),
    handles: z.array(rowSchema)
});
/** Check the original owner and the saved values; an empty successful response is not confirmation. */
export function requestClientHandle(owner, signal, isCurrent, input) {
    return new Promise(resolve => {
        const controller = new AbortController();
        let settled = false;
        let timer;
        const finish = (result) => {
            if (settled)
                return;
            settled = true;
            if (timer !== undefined)
                clearTimeout(timer);
            signal.removeEventListener('abort', cancel);
            controller.abort();
            resolve(result);
        };
        const cancel = () => finish({ data: null, error: 'cancelled' });
        signal.addEventListener('abort', cancel);
        if (signal.aborted || !isCurrent()) {
            cancel();
            return;
        }
        timer = setTimeout(() => finish({ data: null, error: 'unconfirmed' }), HANDLE_TIMEOUT_MS);
        void Promise.resolve().then(() => {
            if (settled || !isCurrent()) {
                cancel();
                return;
            }
            const options = { signal: controller.signal, retryAuth: false };
            // The handle only: its visibility follows "Who can find me" on the server (000226).
            return input ? apiClient.put('/api/contacts/handles/primary', { handle: input.handle }, options)
                : apiClient.get('/api/contacts/handles', options);
        }).then(response => {
            if (settled || !response)
                return;
            if (!isCurrent()) {
                cancel();
                return;
            }
            if (response.status === 401 || response.status === 403) {
                finish({ data: null, error: 'auth' });
                return;
            }
            if (response.status === 409 && response.code === 'HANDLE_TAKEN') {
                finish({ data: null, error: 'taken' });
                return;
            }
            if (response.error || response.status < 200 || response.status >= 300) {
                finish({ data: null, error: 'unconfirmed' });
                return;
            }
            const belongs = (row) => row.client_id === owner.id && row.tenant_id === owner.tenant;
            if (input) {
                const parsed = z.object({ handle: rowSchema }).safeParse(response.data);
                const row = parsed.success ? parsed.data.handle : null;
                finish(row && belongs(row) && row.is_primary && row.handle === input.handle
                    ? { data: { handle: row.handle, visibility: row.visibility }, error: null }
                    : { data: null, error: 'unconfirmed' });
                return;
            }
            const parsed = listSchema.safeParse(response.data);
            if (!parsed.success || parsed.data.identity.id !== owner.id || parsed.data.identity.tenant_id !== owner.tenant
                || !parsed.data.handles.every(belongs) || parsed.data.handles.filter(row => row.is_primary).length > 1) {
                finish({ data: null, error: 'unconfirmed' });
                return;
            }
            const row = parsed.data.handles.find(item => item.is_primary) || parsed.data.handles[0];
            finish({ data: row ? { handle: row.handle, visibility: row.visibility } : { handle: '', visibility: 'public' }, error: null });
        }).catch(() => finish({ data: null, error: 'unconfirmed' }));
    });
}
